import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { financingAPI, domainAPI } from '../lib/api';
import { DollarSign, Shield, FileText, CheckCircle, Loader2, HelpCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function DomainFinancingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { domainId } = useParams();
  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', company: '', message: '' });
  const [myApps, setMyApps] = useState([]);

  useEffect(() => {
    if (domainId) {
      domainAPI.get(domainId).then(r => setDomain(r.data)).catch(() => {});
    }
    if (user) {
      financingAPI.myApplications().then(r => setMyApps(r.data.applications || [])).catch(() => {});
    }
  }, [domainId, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    if (!domainId) { toast.error('Select a domain first'); return; }
    setSubmitting(true);
    try {
      await financingAPI.apply({ domain_id: domainId, ...form });
      setSubmitted(true);
      toast.success('Financing application submitted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Application failed');
    }
    setSubmitting(false);
  };

  const stakePercent = 25;
  const customerPercent = 75;
  const domainPrice = domain?.price || 0;
  const financed = Math.round(domainPrice * customerPercent / 100);
  const monthly = Math.round(financed / 12);

  return (
    <div className="px-6 md:px-12 max-w-6xl mx-auto py-12 md:py-20" data-testid="financing-page">
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-2">
          <DollarSign className="h-6 w-6 text-[#00E5FF]" />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tight">Domain Financing</h1>
        </div>
        <p className="text-zinc-500 mt-2">Can't afford a premium domain outright? We'll finance it for you.</p>
      </div>

      {/* How it works */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {[
          { icon: Shield, title: 'We Co-Own', desc: `XDomainX retains ${stakePercent}% ownership stake. You own ${customerPercent}% from day one.` },
          { icon: FileText, title: 'Contract Based', desc: 'Legally binding contract protects both parties. Domain must be in our registry (NameSilo).' },
          { icon: DollarSign, title: '12 Monthly Payments', desc: 'Pay off your share over 12 months. Full ownership transfers after final payment.' },
        ].map((item, i) => (
          <div key={i} className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm" data-testid={`financing-step-${i}`}>
            <item.icon className="h-8 w-8 text-[#00E5FF] mb-4" />
            <h3 className="text-lg font-bold text-white font-['Outfit'] mb-2">{item.title}</h3>
            <p className="text-sm text-zinc-500">{item.desc}</p>
          </div>
        ))}
      </div>

      {/* Conditions */}
      <div className="p-6 bg-[#0A0A0A] border border-[#00E5FF]/20 rounded-sm mb-12">
        <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Financing Conditions</h3>
        <ul className="space-y-2 text-sm text-zinc-400">
          <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#00E5FF] mt-0.5 shrink-0" /> Domain must be registered in our registry (NameSilo managed)</li>
          <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#00E5FF] mt-0.5 shrink-0" /> XDomainX retains {stakePercent}% ownership until full payment</li>
          <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#00E5FF] mt-0.5 shrink-0" /> 12-month payment plan with fixed monthly installments</li>
          <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#00E5FF] mt-0.5 shrink-0" /> Legal contract signed by both parties</li>
          <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#00E5FF] mt-0.5 shrink-0" /> XDomainX responsible for domain management during financing</li>
          <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#00E5FF] mt-0.5 shrink-0" /> Full ownership transferred after final payment</li>
        </ul>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Calculator / Domain Info */}
        <div>
          {domain ? (
            <div className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm mb-6" data-testid="financing-calculator">
              <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Financing Calculator</h3>
              <div className="space-y-4">
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-zinc-500">Domain</span>
                  <span className="text-white font-bold">{domain.full_name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-zinc-500">Total Price</span>
                  <span className="text-white font-bold">${domainPrice.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-zinc-500">Your Share ({customerPercent}%)</span>
                  <span className="text-[#00E5FF] font-bold">${financed.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-zinc-500">Company Stake ({stakePercent}%)</span>
                  <span className="text-amber-400 font-bold">${(domainPrice - financed).toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 bg-[#00E5FF]/5 px-3 rounded-sm">
                  <span className="text-white font-bold">Monthly Payment</span>
                  <span className="text-[#00E5FF] font-bold text-xl">${monthly.toLocaleString()}/mo</span>
                </div>
                <p className="text-xs text-zinc-600">12 monthly installments over 1 year</p>
              </div>
            </div>
          ) : (
            <div className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm mb-6 text-center">
              <DollarSign className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 mb-4">Select a domain from our marketplace to see financing options</p>
              <Button onClick={() => navigate('/auctions')} className="bg-[#00E5FF] text-black font-bold rounded-sm">
                Browse Auctions <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}

          {/* My Applications */}
          {myApps.length > 0 && (
            <div className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm" data-testid="my-financing-apps">
              <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">My Applications</h3>
              {myApps.map(app => (
                <div key={app.id} className="flex justify-between items-center py-3 border-b border-white/5">
                  <div>
                    <span className="text-white font-medium">{app.domain_name}</span>
                    <span className="text-xs text-zinc-600 ml-2">${app.domain_price?.toLocaleString()}</span>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-sm ${
                    app.status === 'approved' ? 'bg-green-500/10 text-green-400' :
                    app.status === 'rejected' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'
                  }`}>{app.status?.toUpperCase()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Application Form */}
        <div className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm">
          {submitted ? (
            <div className="text-center py-8" data-testid="financing-success">
              <CheckCircle className="h-16 w-16 text-[#00E5FF] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white font-['Outfit'] mb-2">Application Submitted!</h3>
              <p className="text-zinc-500">Our team will review your application and contact you within 48 hours.</p>
            </div>
          ) : (
            <>
              <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Apply for Financing</h3>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="financing-form">
                <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Full Name</Label>
                  <Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} required
                    className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm" data-testid="financing-name" /></div>
                <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required
                    className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm" data-testid="financing-email" /></div>
                <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Phone</Label>
                  <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                    className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm" data-testid="financing-phone" /></div>
                <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Company (optional)</Label>
                  <Input value={form.company} onChange={e => setForm({...form, company: e.target.value})}
                    className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm" data-testid="financing-company" /></div>
                <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Message</Label>
                  <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={3}
                    className="mt-1 w-full bg-[#050505] border border-zinc-800 text-white rounded-sm p-3 text-sm focus:border-[#00E5FF] outline-none" data-testid="financing-message" /></div>
                <Button type="submit" disabled={submitting || !domainId}
                  className="w-full h-12 bg-[#00E5FF] text-black font-bold rounded-sm" data-testid="financing-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {domainId ? 'Submit Application' : 'Select a domain first'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
